// TODO ECOMDUTI-22283 [TechDebt] - Desacoplar funcionalidad de Geolocation de IdtEcommerceApiModule
// Librerias
import { Injectable } from '@angular/core';
import { GeolocationService } from '@ng-web-apis/geolocation';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, catchError, first, filter } from 'rxjs/operators';

// Modelos, interfaces, servicios y transformers
import { Position } from '../models/position.interface';

import { ApiCoordinates } from '@api/_api/api-geolocation/models/api-coordinates.model';
import { ApiGeolocationService } from '@api/_api/api-geolocation/services/api-geolocation.service';
import { Coordinates } from '@api/geolocation/models/coordinates.model';
import { GeocodeRequestParameters } from '@api/geolocation/models/geocode-request-parameters.model';
import { GeolocationInterface } from '@api/geolocation/models/geolocation.interface';
import { Geolocation } from '@api/geolocation/models/geolocation.model';
import { GeolocationTransformer } from '@api/geolocation/transformers/geolocation.transformer';
import { MarketConfigurable } from '@core/domain/impl/market-configurable.impl';
import { getCmsConfigValue, getCountryCode, getLibraryStatus } from '@core/infrastructure/state/core.selectors';
import { BaiduService } from '@shared/map/types/baidu/baidu.service';
import { GoogleService } from '@shared/map/types/google/google.service';

/**
 * Servicio para funcionalidades de geolocalización
 */
@Injectable()
export class GeolocationServices extends MarketConfigurable<any> implements GeolocationInterface {
    protected readonly config: Record<string, Record<string, any>> = {
        IC: {
            service: this.googleService,
            transformer: GeolocationTransformer.coordinatesFromGoogleGeocodeService,
            countryCodeFixer: (_countryCode) => 'ES',
        },
        CN: {
            service: this.baiduService,
            transformer: GeolocationTransformer.coordinatesFromBaiduService,
            countryCodeFixer: (countryCode) => countryCode,
        },
        default: {
            service: this.googleService,
            transformer: GeolocationTransformer.coordinatesFromGoogleGeocodeService,
            countryCodeFixer: (countryCode) => countryCode,
        },
    };

    private readonly countryCode$ = this.store.select(getCountryCode).pipe(
        first(),
        map((countryCode) => {
            const config = this.getConfigByMarketCode(countryCode);

            return config.countryCodeFixer(countryCode);
        }),
    );

    // Definición de propiedades
    public coordinates: Geolocation;

    constructor(
        private readonly apiGeolocationService: ApiGeolocationService,
        private readonly baiduService: BaiduService,
        private readonly googleService: GoogleService,
        private readonly geolocation$: GeolocationService,
        private readonly store: Store,
    ) {
        super();
    }

    /**
     * Obtiene coordenadas a partir de una cadena de texo
     * @param text Cadena de texto con el código postal o la ciudad
     * @returns Objeto de tipo "Coordinates" transformado a partir del modelo "ApiGoogleGeocode"
     */
    public getCoordinatesFromString(text: string): Observable<Geolocation> {
        return this.countryCode$.pipe(
            map((countryCode) => {
                // Definición de parámetros de búsqueda
                const requestParameters: GeocodeRequestParameters = {
                    countryCode,
                    address: text,
                };
                return requestParameters;
            }),
            switchMap((requestParameters) => {
                return this.geocode(requestParameters);
            }),
        );
    }

    private geocode(requestParameters: GeocodeRequestParameters): Observable<Geolocation> {
        const config = this.getConfigByMarketCode(requestParameters.countryCode);

        return config.service.geocodeFromString(requestParameters.address, requestParameters.countryCode).pipe(
            map((geocodeServiceCoordinates) => {
                // Transforma los datos recibidos
                if (geocodeServiceCoordinates) {
                    this.coordinates = config.transformer(geocodeServiceCoordinates);

                    return this.coordinates;
                }
            }),
        );
    }

    /**
     * Obtiene coordenadas a partir de una localización
     * @returns Objeto de tipo "Coordinates" transformado a partir del modelo "ApiGoogleGeocode"
     */
    public getCoordinatesFromLocalization(): Observable<Coordinates> {
        // @TODO: crear servicio deviceService para saber tipo de dispositivo
        // @TODO: crear servicio broweserService para saber q navegador es
        // @TODO: if (_this.isMobile === false && _this.isFirefox === true && navigator.permissions) devolver error

        // Obtenemos las coordenadas desde una geoposición
        return this.apiGeolocationService.getCoordinatesFromGeoPosition().pipe(
            map(
                (positionCoordinates: Position): Coordinates => {
                    // Transforma los datos recibidos
                    this.coordinates = GeolocationTransformer.coordinatesFromGeoPosition(positionCoordinates);

                    return this.coordinates;
                },
            ),
        );
    }

    public getCoordinatesFromIP(): Observable<Coordinates> {
        return this.apiGeolocationService.getCoordinatesFromAPI().pipe(
            map(
                (positionCoordinates: ApiCoordinates) =>
                    GeolocationTransformer.coordinatesService(positionCoordinates),
            ),
        );
    }

    public getCoordinates(): Observable<Coordinates> {
        return this.geolocation$.pipe(
            first(),
            map((position) => GeolocationTransformer.coordinatesFromGeoPosition(position)),
            catchError((_error) => this.getCoordinatesFromIP()),
            switchMap((coordinates) => this.fixCoordinatesToCountry(coordinates)),
        );
    }

    // ECOMDUTI-14878 Se cambian las coordenadas si no coinciden con el país de la store
    private fixCoordinatesToCountry(coordinates: Coordinates): Observable<Coordinates> {
        return this.store.select(getCountryCode).pipe(
            this.loadMapLibraries.bind(this),
            switchMap((countryCode) => {
                // Si las coordenadas son devueltas por back,
                if (coordinates.countryCode) {
                    // Comprobamos si el pais de la store no coincide con el de las coordenadas devueltas por back,
                    // devolvemos las coordenadas por defecto de la store
                    if (coordinates.countryCode.toLowerCase() !== countryCode.toLowerCase()) {
                        return this.defaultCountryCoordinates(countryCode);
                    }
                    return of(coordinates);
                }
                // Si las coordenadas son devueltas por el navegador,
                const config = this.getConfigByMarketCode(countryCode);
                const isBaidu = this.isBaiduSevice(config);
                if (isBaidu) {
                    // // TODO ECOMDUTI-00000 implementar en baidu método vacio para mantener compatibilidad y abstraer complejidad
                    return of(coordinates);
                }
                const geocoder: Observable<any> = config.service.geocodeFromCoordinates(coordinates);
                return geocoder.pipe(
                    switchMap((geocoderResponse) => {
                        const geoCountryCode = GeolocationTransformer.countryCodeFromGeocoderResponse(geocoderResponse);
                        // Comprobamos si el pais de la store no coincide con el de las coordenadas
                        if (geoCountryCode?.toLowerCase() !== countryCode.toLowerCase()) {
                            return this.defaultCountryCoordinates(countryCode);
                        }
                        return of(coordinates);
                    }),
                );
            }),
        );
    }

    private defaultCountryCoordinates(countryCode): Observable<Coordinates> {
        return combineLatest([
            this.geocode({ countryCode, address: '' }),
            this.store.select(getCmsConfigValue('default_country_coordinates')),
        ]).pipe(
            map(([geolocation, config]) => {
                return {
                    latitude: config?.lat ? config.lat : geolocation.latitude,
                    longitude: config?.lng ? config.lng : geolocation.longitude,
                    countryCode: countryCode,
                };
            }),
        );
    }

    private loadMapLibraries(countryCode$: Observable<string>) {
        return countryCode$.pipe(
            switchMap((countryCode) => {
                const config = this.getConfigByMarketCode(countryCode);
                config.service.loadMapLibraries();
                const isBaidu = this.isBaiduSevice(config);
                if (isBaidu) {
                    return this.store.select(getLibraryStatus('baiduScripts')).pipe(
                        filter((status) => status?.loaded),
                        map(() => countryCode),
                    );
                }
                return this.store.select(getLibraryStatus('google')).pipe(
                    filter((status) => status?.loaded),
                    map(() => countryCode),
                );
            }),
        );
    }

    private isBaiduSevice(config): boolean {
        return config.service instanceof BaiduService;
    }
}
